import { auth } from '../../../firebase';
import { backgroundRenderers } from './backgroundRenderers';
import { renderStroke, buildSmoothPath, toCanvasStroke } from './strokeRenderer';

/**
 * Notebook pages to PDF, in the browser (BBD-127).
 *
 * A page is built from three kinds of content, each at the page's own size:
 *
 *   - The paper background (lined, grid, ...) is drawn by the editor's
 *     background renderers on an offscreen canvas at about 200 dpi and placed
 *     as an image. Pages of the same size share one image.
 *   - Ink is written as vector paths, with the same curve geometry the editor
 *     draws (buildSmoothPath). Drawing whole pages as images came to roughly
 *     1 MB a page; as paths a page of handwriting is a tenth of that and
 *     stays sharp at any zoom.
 *   - Text is drawn by the browser (so any script and font works) on a small
 *     canvas of its own and placed as an image.
 *
 * Nothing here touches the live canvas, its redrawCanvas() or the editor's
 * React state, and the loop yields between pages so a long notebook does not
 * freeze the tab.
 */

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks';

const EXPORT_DPI = 200;
const CSS_DPI = 96;                 // Page sizes and stroke points are CSS pixels
const PX_TO_PT = 72 / CSS_DPI;
const JPEG_QUALITY = 0.92;
const TEXT_LINE_HEIGHT = 1.2;
// Canvas limits that hold in every browser we ship to (iOS Safari is the lowest)
const MAX_CANVAS_PIXELS = 16 * 1024 * 1024;
const MAX_CANVAS_SIDE = 16384;
const DEFAULT_PAGE_SIZE = { width: 1200, height: 800 };
// The margin the backend keeps around strokes when it grows a page
const CONTENT_PADDING = 50;

const abortError = () => {
    const error = new Error('Export cancelled');
    error.name = 'AbortError';
    return error;
};

const throwIfAborted = (signal) => {
    if (signal?.aborted) throw abortError();
};

// One frame, so the browser can paint and handle input. Background tabs get
// no animation frames, hence the timer.
const nextFrame = () => new Promise((resolve) => {
    const timer = setTimeout(resolve, 100);
    requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
    });
});

const fetchJson = async (path, signal) => {
    const token = await auth.currentUser?.getIdToken();
    const uid = auth.currentUser?.uid;
    if (!token || !uid) throw new Error('Please sign in to export this notebook.');

    let response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': `Bearer ${token}`,
                'Authorization': `${token}`,
                'requesting-user-uid': uid,
            },
            signal,
        });
    } catch (err) {
        if (err?.name === 'AbortError') throw err;
        throw new Error('Could not reach the server. Check your connection and try again.');
    }
    if (!response.ok) throw new Error('The notebook could not be loaded. Please try again.');
    return response.json();
};

/**
 * The size a page is exported at, in CSS pixels.
 *
 * Infinite pages have a real size. Scaling pages are as large as the window
 * they are viewed in, so their content bounds are exported instead.
 */
export const exportPageSize = (page, pageMode, strokes = []) => {
    const infinite = pageMode === 'infinite';
    let width = (infinite ? page.width : page.content_width) || page.width || DEFAULT_PAGE_SIZE.width;
    let height = (infinite ? page.height : page.content_height) || page.height || DEFAULT_PAGE_SIZE.height;

    // A stroke moved after it was drawn can sit outside the saved bounds:
    // grow the page around it rather than cut it off
    let maxX = 0;
    let maxY = 0;
    strokes.forEach((stroke) => {
        (stroke.points || []).forEach((point) => {
            if (point.x > maxX) maxX = point.x;
            if (point.y > maxY) maxY = point.y;
        });
    });
    if (maxX > width) width = maxX + CONTENT_PADDING;
    if (maxY > height) height = maxY + CONTENT_PADDING;

    return { width: Math.ceil(width), height: Math.ceil(height) };
};

// Canvas pixels per CSS pixel: 200 dpi, less for pages too large for a canvas
export const exportScale = ({ width, height }) => Math.min(
    EXPORT_DPI / CSS_DPI,
    Math.sqrt(MAX_CANVAS_PIXELS / (width * height)),
    MAX_CANVAS_SIDE / Math.max(width, height)
);

const loadImage = (url) => new Promise((resolve) => {
    const image = new Image();
    // Without CORS approval the image would taint the canvas and block the export
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
});

// CSS pixels to PDF points
const pt = (value) => value * PX_TO_PT;

const canvasToBytes = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (!blob) {
            reject(new Error('This page is too large to export in this browser.'));
            return;
        }
        blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer)), reject);
    }, type, quality);
});

/**
 * Draw the paper of a page (background and embedded images) onto `canvas`,
 * resizing it to the page.
 */
export const drawPagePaper = (canvas, { size, background, mediaElements = [], backgroundCache }) => {
    const scale = exportScale(size);
    canvas.width = Math.max(1, Math.round(size.width * scale));
    canvas.height = Math.max(1, Math.round(size.height * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This page is too large to export in this browser.');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    // White paper first: some backgrounds only draw their lines, and JPEG has no transparency
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size.width, size.height);
    const renderer = backgroundRenderers[background] || backgroundRenderers.white;
    renderer(ctx, size.width, size.height, backgroundCache);

    mediaElements.forEach((element) => {
        if (element.image) {
            ctx.drawImage(element.image, element.x_position, element.y_position, element.width, element.height);
        }
    });
};

// Any CSS colour as [r, g, b, alpha], read back from a canvas context
const createColorParser = () => {
    const ctx = document.createElement('canvas').getContext('2d');
    const cache = new Map();
    return (color) => {
        if (!cache.has(color)) {
            ctx.fillStyle = '#000000';
            ctx.fillStyle = color;
            const value = ctx.fillStyle;
            let parsed;
            if (value.startsWith('#')) {
                parsed = [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16)).concat(1);
            } else {
                const parts = value.match(/[\d.]+/g).map(Number);
                parsed = [parts[0], parts[1], parts[2], parts.length > 3 ? parts[3] : 1];
            }
            cache.set(color, parsed);
        }
        return cache.get(color);
    };
};

// Lets buildSmoothPath, written for canvas paths, write a path into the PDF.
// PDF has no quadratic curves, so each one becomes the equivalent cubic.
const pdfPath = (pdf) => {
    let currentX = 0;
    let currentY = 0;
    return {
        moveTo(x, y) {
            pdf.moveTo(pt(x), pt(y));
            currentX = x;
            currentY = y;
        },
        lineTo(x, y) {
            pdf.lineTo(pt(x), pt(y));
            currentX = x;
            currentY = y;
        },
        quadraticCurveTo(controlX, controlY, x, y) {
            pdf.curveTo(
                pt(currentX + (2 / 3) * (controlX - currentX)), pt(currentY + (2 / 3) * (controlY - currentY)),
                pt(x + (2 / 3) * (controlX - x)), pt(y + (2 / 3) * (controlY - y)),
                pt(x), pt(y)
            );
            currentX = x;
            currentY = y;
        },
    };
};

// One ink stroke as a vector path. Mirrors the canvas drawing in
// strokeRenderer.js: same curve, colour, width, opacity, caps and joins.
const drawInkStroke = (pdf, stroke, parseColor) => {
    const points = stroke.points;
    if (!points || points.length < 2) return;

    const style = stroke.style || {};
    const [r, g, b, colorAlpha] = parseColor(style.color);
    const opacity = (style.opacity >= 0 && style.opacity <= 1 ? style.opacity : 1) * colorAlpha;
    if (opacity === 0) return;

    pdf.saveGraphicsState();
    pdf.setDrawColor(r, g, b);
    pdf.setLineWidth(pt(style.thickness > 0 ? style.thickness : 1));
    pdf.setLineCap(style.lineCap || 'round');
    pdf.setLineJoin(style.lineJoin || 'round');
    if (opacity < 1) pdf.setGState(new pdf.GState({ 'stroke-opacity': opacity }));

    const path = pdfPath(pdf);
    if (points.length === 2) {
        path.moveTo(points[0].x, points[0].y);
        path.lineTo(points[1].x, points[1].y);
    } else {
        buildSmoothPath(path, points);
    }
    pdf.stroke();
    pdf.restoreGraphicsState();
};

const fontFamily = (family) => (/^[a-z-]+$/i.test(family) ? family : JSON.stringify(family));

const textElementFont = (element) =>
    `${element.is_italic ? 'italic ' : ''}${element.is_bold ? 'bold ' : ''}${element.font_size || 14}px ${fontFamily(element.font_family || 'Arial')}`;

const drawTextElement = (ctx, element) => {
    const fontSize = element.font_size || 14;
    ctx.save();
    ctx.fillStyle = element.color || '#000000';
    ctx.font = textElementFont(element);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    String(element.content || '').split('\n').forEach((line, index) => {
        ctx.fillText(line, element.x_position, element.y_position + index * fontSize * TEXT_LINE_HEIGHT);
    });
    ctx.restore();
};

// The box a piece of text covers on the page, with room for accents and descenders
const textBounds = (measureCtx, { x, y, lines, font, fontSize, rotation = 0 }) => {
    measureCtx.font = font;
    const width = Math.max(1, ...lines.map((line) => measureCtx.measureText(line).width));
    const pad = fontSize * 0.5;
    const box = {
        left: x - pad,
        top: y - pad,
        right: x + width + pad,
        bottom: y + lines.length * fontSize * TEXT_LINE_HEIGHT + pad,
    };
    if (!rotation) return box;

    // Rotated about its anchor, like the canvas draws it
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const corners = [[box.left, box.top], [box.right, box.top], [box.right, box.bottom], [box.left, box.bottom]]
        .map(([cx, cy]) => [x + (cx - x) * cos - (cy - y) * sin, y + (cx - x) * sin + (cy - y) * cos]);
    return {
        left: Math.min(...corners.map((c) => c[0])),
        top: Math.min(...corners.map((c) => c[1])),
        right: Math.max(...corners.map((c) => c[0])),
        bottom: Math.max(...corners.map((c) => c[1])),
    };
};

// Draw text with the browser on a transparent canvas just large enough for
// it, and place that on the page. `draw` works in page coordinates.
const placeText = async (pdf, canvas, bounds, size, alias, draw) => {
    const left = Math.max(0, Math.floor(bounds.left));
    const top = Math.max(0, Math.floor(bounds.top));
    const width = Math.min(size.width, Math.ceil(bounds.right)) - left;
    const height = Math.min(size.height, Math.ceil(bounds.bottom)) - top;
    if (width <= 0 || height <= 0) return;

    const scale = exportScale({ width, height });
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, -left * scale, -top * scale);
    draw(ctx);

    const png = await canvasToBytes(canvas, 'image/png');
    pdf.addImage(png, 'PNG', pt(left), pt(top), pt(width), pt(height), alias, 'FAST');
};

/**
 * Add the content of one notebook page to the current page of `pdf`.
 *
 * content: { strokes, textElements, mediaElements } with strokes in canvas
 *   shape and each media element carrying its loaded `image` (or null).
 * tools: { canvas, parseColor, papers, backgroundCache } shared by all pages
 *   of one export; `pageKey` keeps image names unique within the file.
 */
const addPageContent = async (pdf, { size, content, background, includeBackground, pageKey }, tools) => {
    const { canvas, parseColor, papers, backgroundCache } = tools;
    const media = (content.mediaElements || []).filter((element) => element.image);
    const paper = includeBackground ? background : 'white';

    // Plain white paper with nothing on it needs no image: the PDF page is white
    if (paper !== 'white' || media.length > 0) {
        // Pages of one size share their paper, unless a page has images of its own
        const alias = media.length > 0 ? `paper-${pageKey}` : `paper-${paper}-${size.width}x${size.height}`;
        if (!papers.has(alias)) {
            drawPagePaper(canvas, { size, background: paper, mediaElements: media, backgroundCache });
            papers.set(alias, await canvasToBytes(canvas, 'image/jpeg', JPEG_QUALITY));
        }
        pdf.addImage(papers.get(alias), 'JPEG', 0, 0, pt(size.width), pt(size.height), alias);
    }

    const measureCtx = canvas.getContext('2d');
    const strokes = content.strokes || [];
    for (let index = 0; index < strokes.length; index++) {
        const stroke = strokes[index];
        if (!stroke || !stroke.points || stroke.points.length === 0) continue;

        if (stroke.type === 'text') {
            if (!stroke.text || stroke.text.trim() === '') continue;
            const fontSize = stroke.style.thickness * 8;
            const bounds = textBounds(measureCtx, {
                x: stroke.points[0].x,
                y: stroke.points[0].y,
                lines: [stroke.text],
                font: `${fontSize}px Arial`,
                fontSize,
                rotation: stroke.rotation || 0,
            });
            await placeText(pdf, canvas, bounds, size, `text-${pageKey}-${index}`, (ctx) => renderStroke(ctx, stroke));
        } else {
            drawInkStroke(pdf, stroke, parseColor);
        }
    }

    const textElements = content.textElements || [];
    for (let index = 0; index < textElements.length; index++) {
        const element = textElements[index];
        const bounds = textBounds(measureCtx, {
            x: element.x_position,
            y: element.y_position,
            lines: String(element.content || '').split('\n'),
            font: textElementFont(element),
            fontSize: element.font_size || 14,
        });
        await placeText(pdf, canvas, bounds, size, `element-${pageKey}-${index}`, (ctx) => drawTextElement(ctx, element));
    }
};

// Strokes still syncing (or that failed to) are drawn faded or dashed in the
// editor; the export shows them as finished ink.
const asFinishedStroke = (stroke) => (
    stroke.isPending || stroke.isFailed ? { ...stroke, isPending: false, isFailed: false } : stroke
);

const loadPageContent = async (notebookId, page, localPage, signal) => {
    let strokes;
    let textElements;
    let mediaElements;

    if (localPage) {
        strokes = (localPage.strokes || []).map(asFinishedStroke);
        textElements = localPage.textElements || [];
        mediaElements = localPage.mediaElements || [];
    } else {
        const data = await fetchJson(`/${notebookId}/pages/${page.id}/`, signal);
        strokes = (data.strokes || []).map(toCanvasStroke);
        textElements = data.text_elements || [];
        mediaElements = data.media_elements || [];
    }

    let skippedMedia = 0;
    const loadedMedia = await Promise.all(mediaElements.map(async (element) => {
        const image = element.file_type === 'image' ? await loadImage(element.file_url) : null;
        if (!image) skippedMedia += 1;
        return { ...element, image };
    }));

    return { strokes, textElements, mediaElements: loadedMedia, skippedMedia };
};

/**
 * Export pages of a notebook as one PDF.
 *
 * selection: { from, to } - positions in page order, starting at 1; omit for all pages.
 * includeBackground: draw the paper background (lined, grid, ...) or leave white.
 * background: the background the editor shows now; defaults to the saved one.
 * localPages: Map of page id -> the editor's page object, for pages it has
 *   loaded. Their strokes are used as they are on screen, unsaved ones included.
 * onProgress({ done, total }) is called before each page and once at the end.
 * signal: an AbortSignal; the export rejects with an AbortError when it fires.
 *
 * Resolves to { blob, title, pageCount, skippedMedia }.
 */
export const exportNotebookPdf = async ({ notebookId, selection = null, includeBackground = true, background = null, localPages = null, onProgress = null, signal = null }) => {
    const notebook = await fetchJson(`/${notebookId}/`, signal);
    const allPages = [...(notebook.pages || [])].sort((a, b) => a.page_number - b.page_number);

    const from = Math.max(1, selection?.from || 1);
    const to = Math.min(allPages.length, selection?.to || allPages.length);
    const pages = allPages.slice(from - 1, to);
    if (pages.length === 0) throw new Error('There are no pages in that range.');

    const { jsPDF } = await import('jspdf');
    throwIfAborted(signal);

    const pageBackground = background || notebook.background_type || 'white';
    const canvas = document.createElement('canvas');
    const tools = {
        canvas,
        parseColor: createColorParser(),
        papers: new Map(),
        backgroundCache: { width: 0, height: 0, stars: [] },
    };
    let pdf = null;
    let skippedMedia = 0;

    try {
        for (let index = 0; index < pages.length; index++) {
            throwIfAborted(signal);
            if (onProgress) onProgress({ done: index, total: pages.length });

            const page = pages[index];
            const content = await loadPageContent(notebookId, page, localPages?.get(page.id) || null, signal);
            throwIfAborted(signal);
            skippedMedia += content.skippedMedia;

            const size = exportPageSize(page, notebook.page_mode, content.strokes);
            const width = pt(size.width);
            const height = pt(size.height);
            // jsPDF swaps the two numbers unless the orientation agrees with them
            const orientation = width > height ? 'landscape' : 'portrait';
            if (!pdf) {
                pdf = new jsPDF({ unit: 'pt', format: [width, height], orientation, compress: true, floatPrecision: 2 });
                pdf.setProperties({ title: notebook.title || 'Notebook' });
            } else {
                pdf.addPage([width, height], orientation);
            }

            await addPageContent(pdf, { size, content, background: pageBackground, includeBackground, pageKey: index }, tools);

            // Let the browser paint the progress and stay responsive
            await nextFrame();
        }
    } finally {
        // Give the canvas memory back right away
        canvas.width = 0;
        canvas.height = 0;
    }

    throwIfAborted(signal);
    if (onProgress) onProgress({ done: pages.length, total: pages.length });

    return {
        blob: pdf.output('blob'),
        title: notebook.title || 'Notebook',
        pageCount: pages.length,
        skippedMedia,
    };
};

export const notebookPdfFileName = (title, selection, totalPages) => {
    const base = String(title || 'Notebook').replace(/[\\/:*?"<>|]/g, '_').trim() || 'Notebook';
    if (!selection || (selection.from <= 1 && selection.to >= totalPages)) return `${base}.pdf`;
    if (selection.from === selection.to) return `${base} (page ${selection.from}).pdf`;
    return `${base} (pages ${selection.from}-${selection.to}).pdf`;
};
